<template>
	<Modal
		:id="elementId" :title="trans('users.labels.update_site')" :has-form="true" @submit="onFormSubmit"
		@clear="onFormClear" @close="onFormClear">
		<UiForm
			:id="'siteupdateform_'+elementId"
			:classes="'needs-validation'" :type="'update'" :data-bs-dismiss="formValidated" @submit="onFormSubmit()"
			@clear="onFormClear" @close="onFormClear">
			<TextInput
				v-model="form.name" type="text" :required="true"
				:label="'Woodland or Site ' + trans('users.labels.name')" :name="'siteupdatename_'+elementId" class="mb-2" />
			<TextInput
				v-model="form.gridref" :force-upper-case="true" type="text" :title="'8-figure grid reference'"
				:pattern="'[A-Za-z]{2}[0123456789]{8}'" :required="true" :label="trans('users.labels.gridref')" :name="'updatesitegridref_'+elementId"
				class="mb-2" />
			<div v-if="!validateGridRef_8()">
				<span style="color:red">Please enter a valid format (2 letters + 8 numbers, no spaces).</span>
			</div>
			<br v-else>
			<small>{{ trans('users.labels.gridref_sub') }}<br>Use <a
				href="https://magic.defra.gov.uk/MagicMap.html" target="_blank">MAGIC's</a> locator tool if you are unsure.
				MAGIC will generate an eight digit grid reference.
				<!-- To convert to a six figure grid reference please delete the 4th and 8th digit e.g. 123<span style="color:red">4</span>567<span style="color:red">8</span> would give a grid reference of 123567 -->
			</small>
			<Dropdown
				:v-model="form.county"
				name="select_county_dropdown"
				:required="true"
				:label="trans('global.phrases.select_county')"
				:options="countiesListWCA"
				:placeholder="form.county"
				:multiple="false"
				class="my-2"
				@input="updateCounty"/>
			<hr>
			<TextInput
				v-model="form.single_business_id" type="text" :required="false"
				:label="trans('users.labels.single_business_id')+' ('+trans('global.phrases.optional')+')'" :name="'updatesitesbi_'+elementId"
				class="mb-2" />
			<TextInput
				v-model="form.higher_tier_ref" type="text" :required="false"
				:label="trans('users.labels.higher_tier_ref')+' ('+trans('global.phrases.optional')+')'" :name="'updatesitehtr_'+elementId"
				class="mb-2" />
			<TextInput
				v-model="form.woodland_man_plan" type="text" :required="false"
				:label="trans('users.labels.woodland_man_plan')+' ('+trans('global.phrases.optional')+')'" :name="'updatesitewmp_'+elementId"
				class="mb-2" />
		</UiForm>
	</modal>
</template>

<script lang="ts" setup>
	import AuthService from "@/services/AuthService";
	import { trans } from "@/helpers/i18n";
	import { defineComponent, reactive, onMounted, computed, watch } from "vue";
	// import { getResponseError } from "@/helpers/api";
	import { useAuthStore } from "@/stores/auth";
	// import { useAlertStore } from "@/stores";
	import UiButton from "@/views/components/input/UiButton.vue";
	import TextInput from "@/views/components/input/TextInput.vue";
	import Dropdown from "@/views/components/input/Dropdown.vue";
	import Modal from "@/views/components/Modal.vue";
	import UiForm from "@/views/components/UiForm.vue";
	import { useSiteStore, countiesListWCA } from "@/stores/site";

	const authService = new AuthService();
	// const alertStore = useAlertStore();
	const authStore = useAuthStore();
	const siteStore = useSiteStore();

	const form = reactive({
		name: "",
		gridref: "",
		single_business_id: "",
		higher_tier_ref: "",
		woodland_man_plan: "",
		county: ""
	});

	const props = defineProps<{
		site: any;
		elementId:string;
	}>();

	onMounted(() => {
		if (!authStore.user) {
			return;
		}
		onFormClear();
	});

	async function onFormClear () {
		form.name = props.site.name || "NONE";
		// update any old 6 figure grid refs to 8 figure
		if (props.site.gridref.length == 8)
			props.site.gridref = props.site.gridref.slice(0, 5) + "0" + props.site.gridref.slice(5) + "0";
		form.gridref = props.site.gridref;
		form.single_business_id = props.site.single_business_id || "NONE";
		form.higher_tier_ref = props.site.higher_tier_ref || "NONE";
		form.woodland_man_plan = props.site.woodland_man_plan || "NONE";
		form.county = props.site.county;

	}

	function validateGridRef_8 () {
		if (form.gridref.length > 10) return false;
		if (form.gridref.includes(" ")) return false;
		if (form.gridref.includes("-")) return false;
		// Regular expression for 2 letters followed by 6 numbers
		// const pattern = /^((([sS]|[nN])[a-hA-Hj-zJ-Z])|(([tT]|[oO])[abfglmqrvwABFGLMQRVW])|([hH][l-zL-Z])|([jJ][lmqrvwLMQRVW]))\s?([0-9]{3})\s?([0-9]{3})$/;
		const pattern = /^[A-Za-z]{2}[0123456789]{8}$/;
		// Test if the input matches the pattern
		return pattern.test(form.gridref);
	}

	function validateForm () {
		return (!(form.name == "") && validateGridRef_8());
	}
	const formValidated = computed (() => {
		return validateForm();
	});

	watch(() => props.site, (newVal) => {
		onFormClear();
	});
	async function onFormSubmit () {
		if (form.gridref.includes(" ")) form.gridref = form.gridref.replace(/\s/g, "");
		if (form.gridref.length > 10) form.gridref = form.gridref.substring(0, 10);
		if (formValidated.value) {
			props.site.name = form.name;
			props.site.gridref = form.gridref;
			props.site.single_business_id = form.single_business_id;
			props.site.higher_tier_ref = form.higher_tier_ref;
			props.site.woodland_man_plan = form.woodland_man_plan;
			props.site.county = form.county;
			props.site.updated_at = new Date().toISOString();//.replace("T", " ");
			await siteStore.update(props.site);
			onFormClear();
		}
	}

	function updateCounty (newCounty: string) {
		form.county = newCounty;
	}
</script>
